/**
 * One channel, end to end: its numbers, its campaigns and its templates.
 *
 * Email, WhatsApp, SMS and Push are the same page with a different `channel`,
 * so there is one component rather than four near-identical ones.
 *
 * The work is the campaign list, so the campaign list gets the page. Setup,
 * reach-by-audience and the opt-out list used to sit in a column down the
 * right, which cost the table a third of its width for three panels nobody
 * comes here to read - setup is a once-a-quarter job, and both it and the
 * opt-out list have their own screens. Each headline figure opens the rows it
 * counted, because "Replied 2" with no way through to *which two* is a dead
 * end.
 */
import { useMemo, useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import {
  Card, CardHead, EmptyState, Avatar
} from '../../components/Primitives.jsx';
import { TileRow } from '../../components/StatCards.jsx';
import { channelLabel, channelIcon, channelTone } from '../../constants/marketing.js';
import { audienceOf, reachable, campaignStats, scheduleState } from '../../domain/marketing.js';
import { CampaignStatusPill } from './MarketingBits.jsx';
import CampaignFilters, { NO_FILTERS, applyFilters } from './CampaignFilters.jsx';
import { looksHtml, htmlToText } from '../../components/modals/CampaignEditor.jsx';

/**
 * The first words of a template, for a list.
 *
 * An email campaign begins `<table role="presentation" cellpadding="0"...`, so
 * a straight slice of the body showed a column of identical markup and told
 * you nothing about which template was which.
 */
const excerpt = (body, n) => {
  const words = (looksHtml(body) ? htmlToText(body) : String(body || ''))
    .replace(/\s+/g, ' ')
    .trim();
  return words ? words.slice(0, n) + (words.length > n ? '…' : '') : '';
};

export default function ChannelPage({ channel }) {
  const { mkt, mktCtx, fmtMoney, setSheet, removeMarketingRecord } = useApp();

  const label = channelLabel(channel);
  const tone = channelTone(channel);
  const campaigns = mkt.campaigns.filter(c => c.channel === channel);

  /* Newest first, and narrowable — this screen used to list every campaign
     ever written on the channel in whatever order they happened to be saved. */
  const [filters, setFilters] = useState(NO_FILTERS);
  const shown = useMemo(
    () => applyFilters(campaigns, filters, c => campaignStats(c, mktCtx)),
    [campaigns, filters, mktCtx]
  );
  const templates = mkt.templates.filter(t => t.channel === channel);

  const isWa = channel === 'whatsapp';

  /* One pass over the channel's campaigns, memoised: each campaignStats scans
     the message logs, and without this it re-ran on every render - including
     opening a popup, which is what made navigation feel slow. */
  const agg = useMemo(() => {
    let sent = 0; let delivered = 0; let read = 0; let failed = 0; let replied = 0; let revenue = 0;
    campaigns.forEach(c => {
      const st = campaignStats(c, mktCtx);
      sent += st.sent;
      delivered += st.delivered || 0;
      read += st.read || 0;
      failed += st.failed || 0;
      replied += st.replied;
      revenue += st.revenue;
    });
    return {
      sent, delivered, read, failed, replied, revenue,
      rate: sent ? Math.round(replied / sent * 100) : null,
      reachable2: reachable(mkt.contacts || [], channel, mktCtx).length
    };
  }, [campaigns, mktCtx, channel, mkt.contacts]);

  const { sent, delivered, read, failed, replied, revenue, rate, reachable2 } = agg;

  /* Each figure opens the rows behind it. Reach and revenue have no such
     sheet, so those two stay plain rather than being buttons that do nothing. */
  const open = metric => () => setSheet({ kind: 'marketingDetail', metric, channel });

  const removeCampaign = (e, id) => {
    e.stopPropagation();
    // eslint-disable-next-line no-alert
    if (window.confirm('Delete this campaign?')) removeMarketingRecord('campaigns', id);
  };

  const removeTemplate = (e, id) => {
    e.stopPropagation();
    // eslint-disable-next-line no-alert
    if (window.confirm('Delete this template?')) removeMarketingRecord('templates', id);
  };

  return (
    <>
      <TileRow
        tiles={isWa
          ? [
            ['Campaigns', campaigns.length, channelIcon(channel), tone, open('campaigns')],
            ['Sent', sent, 'send', 'g-green', open('sent')],
            ['Delivered', delivered, 'check', 'g-green'],
            ['Read', read, 'eye', 'g-purple'],
            ['Failed', failed, 'x', failed ? 'g-red' : 'g-slate'],
            ['Replied', replied, 'chat', 'g-cyan', open('replied')]
          ]
          : [
            ['Campaigns', campaigns.length, channelIcon(channel), tone, open('campaigns')],
            ['Reachable', reachable2, 'users', 'g-blue'],
            ['Sent', sent, 'send', 'g-green', open('sent')],
            ['Replied', replied, 'chat', 'g-cyan', open('replied')],
            ['Reply rate', rate === null ? '—' : `${rate}%`, 'chart', 'g-purple', open('replied')],
            ['Revenue after', fmtMoney(revenue), 'card', 'g-amber']
          ]}
      />

      <div className="onecol">
        <div>
          <Card>
            <CardHead icon={channelIcon(channel)} tone={tone} title={`${label} campaigns`} count={campaigns.length}>
              <button
                type="button"
                className="btn sm primary"
                onClick={() => setSheet({ kind: 'campaign', preset: channel })}
              >
                <Icon name="plus" />New
              </button>
            </CardHead>

            <div style={{ padding: '0 15px' }}>
              <CampaignFilters
                all={campaigns}
                shown={shown.length}
                value={filters}
                onChange={setFilters}
                audiences={mkt.audiences}
                /* Every campaign here is on this channel already. */
                channels={false}
              />
            </div>

            {shown.length ? (
              <div className="tscroll">
                <table>
                  <thead>
                    <tr>
                      <th>Campaign</th><th>Audience</th><th>Status</th>
                      <th>Reach</th><th>Sent</th><th>Replied</th><th>When</th><th />
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map(c => {
                      const st = campaignStats(c, mktCtx);
                      const audience = mkt.audiences.find(a => a.id === c.audience);
                      /* Scheduled and past its time — which nothing sends on
                         its own, so the row has to say so. */
                      const due = scheduleState(c);
                      return (
                        <tr key={c.id} onClick={() => setSheet({ kind: 'campaignView', id: c.id })}>
                          <td>
                            <div className="cname">
                              <Avatar name={c.name} />
                              <div><b>{c.name}</b><small>{c.subject || c.goal || ''}</small></div>
                            </div>
                          </td>
                          <td className="dim">{audience
                          ? audience.name
                          : (c.contacts || []).length
                            ? `${(c.contacts || []).length} chosen`
                            : '—'}</td>
                          <td><CampaignStatusPill status={c.status} /></td>
                          <td><b>{st.reach}</b></td>
                          <td>{st.sent}</td>
                          <td>{st.replied}</td>
                          <td>
                            <div className="whenc">
                              <span className="dim">{c.schedule || c.created || ''}</span>
                              {due ? <span className={`duep ${due.tone}`}>{due.label}</span> : null}
                            </div>
                          </td>
                          <td>
                            <div className="acts">
                              <button
                                type="button"
                                /* Once it has gone out, "send" is the wrong
                                   word for the same button: whoever presses it
                                   is sending the message a second time, and
                                   should be told so before they do. */
                                title={st.sent ? 'Send it again' : 'Send console'}
                                onClick={e => { e.stopPropagation(); setSheet({ kind: 'send', id: c.id }); }}
                              >
                                <Icon name={st.sent ? 'refresh' : 'send'} />
                              </button>
                              <button
                                type="button"
                                title="Edit"
                                onClick={e => { e.stopPropagation(); setSheet({ kind: 'campaign', id: c.id }); }}
                              >
                                <Icon name="edit" />
                              </button>
                              <button type="button" className="rm" title="Delete" onClick={e => removeCampaign(e, c.id)}>
                                <Icon name="trash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState
                icon={channelIcon(channel)}
                tone={tone}
                title={campaigns.length ? 'Nothing matches that' : `No ${label} campaigns yet`}
              >
                {campaigns.length
                  ? 'No campaign here matches those filters.'
                  : 'Pick an audience and send your first one.'}
              </EmptyState>
            )}
          </Card>

          <Card>
            <CardHead icon="file" tone="g-orange" title={`${label} templates`} count={templates.length}>
              <button
                type="button"
                className="btn sm"
                onClick={() => setSheet({ kind: 'template', preset: channel })}
              >
                <Icon name="plus" />New
              </button>
            </CardHead>

            {templates.length ? (
              <div className="tscroll">
                <table>
                  <thead><tr><th>Template</th><th>Subject</th><th>Message</th><th /></tr></thead>
                  <tbody>
                    {templates.map(t => (
                      <tr key={t.id} onClick={() => setSheet({ kind: 'template', id: t.id })}>
                        <td><b>{t.name}</b></td>
                        <td className="dim">{t.subject || '—'}</td>
                        <td className="dim" style={{ whiteSpace: 'normal', maxWidth: 260 }}>
                          {excerpt(t.body, 90) || '—'}
                        </td>
                        <td>
                          <div className="acts">
                            <button
                              type="button"
                              title="Edit"
                              onClick={e => { e.stopPropagation(); setSheet({ kind: 'template', id: t.id }); }}
                            >
                              <Icon name="edit" />
                            </button>
                            <button type="button" className="rm" title="Delete" onClick={e => removeTemplate(e, t.id)}>
                              <Icon name="trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty" style={{ padding: 22 }}>
                <b>Nothing yet</b>Write a {label} template to reuse.
              </div>
            )}
          </Card>
        </div>

      </div>
    </>
  );
}
